import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col, Input, Row, Tooltip, Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { GiSoundWaves } from 'react-icons/gi';
import { MdCancel } from 'react-icons/md';
import { FaMicrophone, FaRecordVinyl } from 'react-icons/fa';
import { IoAttach, IoCalendarSharp, IoSend } from 'react-icons/io5';
import { useDispatch, useSelector } from 'react-redux';

import ComposerAttachments from './ComposerAttachments';
import ComposerEmojiPicker from './ComposerEmojiPicker';
import MentionSuggestions from './MentionSuggestions';
import Text from '../Text';
import { Colors, Utils } from '../../config';
import { MENTION_RESULT_CAP, TYPING_IDLE_MS } from '../../config/constants';
import { hasMention } from '../../config/mentions';
import { applyMention, findMentionQuery } from '../../config/mentions/trigger';
import { isRealtimeUsable, SOCKET_DOWN_MESSAGE } from '../../config/socket/socket';
import { MsgAction } from '../../store/actions';

/**
 * The group composer: draft text, attachments, voice note, @mention picker, send.
 *
 * WHY THIS IS ITS OWN COMPONENT — every piece of state below used to live in
 * `GroupMessages`, the same component that renders the message stream. So:
 *
 * - `messageInput` changed on every keystroke, re-rendering the whole pane and with it
 *   every loaded message bubble — roughly 600 lines of JSX per message, rebuilt per
 *   keypress. That was the app's most visible performance problem.
 * - `elapsedTime` ticks once a second while recording a voice note, so for the whole
 *   60-second window the entire message list re-rendered every second.
 * - The `typing` indicator and the mention picker did the same on a smaller scale.
 *
 * None of that state is read by the stream. Moving it below the stream in the tree
 * means a keystroke now re-renders this subtree only. The message list is untouched.
 *
 * Redux is read directly here rather than passed down, so the parent does not
 * re-render when an upload resolves either.
 *
 * @param channelId       target channel
 * @param isSecret        secret groups block social links / 9-digit UIDs and cap length
 * @param canPost         false in a broadcast channel the viewer cannot post to
 * @param userData        the signed-in user (sender_id)
 * @param socket          shared socket; a parked one must not be emitted into
 * @param mentionRoster   `{ id, name, image }[]` — excludes the viewer
 * @param mentionsEnabled false in a 1:1 or secret group, where mentions do not exist
 * @param replyOnMsg      the message being replied to, owned by the parent (the stream
 *                        sets it), cleared through `onClearReply`
 * @param emitTyping      `(channelId, boolean) => void`, shared with the thread composer
 * @param onSent          called after a successful send, so the parent can clear its
 *                        unread divider and bump its scroll-to-latest trigger
 * @param onOpenSchedule  opens the parent's schedule-message modal
 */
const GroupComposer = ({
  channelId,
  isSecret,
  canPost = true,
  userData,
  socket,
  mentionRoster,
  mentionsEnabled,
  replyOnMsg,
  onClearReply,
  emitTyping,
  onSent,
  onOpenSchedule,
}) => {
  const dispatch = useDispatch();

  // Selected per value, not as a slice — see the note in GroupMessages.
  const uploadMediaLoader = useSelector((state) => state?.MsgReducer?.upload_chat_loader);
  const chatMedia = useSelector((state) => state?.MsgReducer?.upload_chat_media?.data?.chatMedia);

  const [messageInput, setMessageInput] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  /** Mentions in the draft as `{ id, name }` — from the picker, never parsed back out. */
  const [mentions, setMentions] = useState([]);
  /** The `@` token the caret is inside, as `{ at, query }`, or null. */
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionActive, setMentionActive] = useState(0);

  const inputRef = useRef(null);
  const audioChunks = useRef([]);
  const intervalRef = useRef(null);
  const typingStopTimer = useRef(null);
  /**
   * The live microphone capture, so it can be released on the way out.
   *
   * `recorder.onstop` releases it on the normal path. This ref covers the path it
   * cannot: unmounting while still recording never calls `stop()`, so `onstop` never
   * fires and the capture would stay open — with the browser's recording indicator
   * lit — for the life of the tab.
   */
  const streamRef = useRef(null);

  // Leaving the conversation must not leave a recording timer running, and must not
  // carry a half-typed draft into the next one.
  useEffect(() => {
    setMessageInput('');
    setSelectedImages([]);
    setAudioUrl(null);
    setMentions([]);
    setMentionQuery(null);
  }, [channelId]);

  useEffect(
    () => () => {
      clearInterval(intervalRef.current);
      clearTimeout(typingStopTimer.current);
      // Last resort for the microphone — see the note on `streamRef`.
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    },
    []
  );

  // Attachments upload as soon as they are picked, so the send is just an emit.
  useEffect(() => {
    if (selectedImages.length === 0) return;
    const formData = new FormData();
    selectedImages.forEach((file) => formData.append('media', file));
    dispatch(MsgAction.UploadChatMedia(formData));
  }, [selectedImages, dispatch]);

  const uploadChatMedia = useCallback(
    (formData) =>
      new Promise((resolve, reject) => {
        dispatch(
          MsgAction.UploadChatMedia(formData, (data) => {
            const res = data?.data?.chatMedia;
            if (res) resolve(res);
            else reject(new Error('Failed to get chatMedia'));
          })
        );
      }),
    [dispatch]
  );

  // ── mentions ───────────────────────────────────────────────────────────────

  const syncMentionQuery = useCallback(
    (text, caret) => {
      if (!mentionsEnabled) {
        setMentionQuery(null);
        return;
      }
      setMentionQuery(findMentionQuery(text, caret));
      setMentionActive(0);
    },
    [mentionsEnabled]
  );

  /**
   * Roster rows for the current token — derived, never stored.
   *
   * Ranked prefix-first then alphabetically: a plain `includes` filter put "Amelia"
   * above "Mia" for the query "mi". Capped, because this used to render one row per
   * match — the first `@` in a large channel painted the whole membership.
   */
  const filteredUsers = useMemo(() => {
    if (!mentionQuery) return [];
    const q = mentionQuery.query.trim().toLowerCase();
    const roster = mentionRoster ?? [];
    if (!q) return roster.slice(0, MENTION_RESULT_CAP);

    return roster
      .map((user) => ({ user, hit: user.name.toLowerCase().indexOf(q) }))
      .filter(({ hit }) => hit !== -1)
      .sort((a, b) => a.hit - b.hit || a.user.name.localeCompare(b.user.name))
      .slice(0, MENTION_RESULT_CAP)
      .map(({ user }) => user);
  }, [mentionQuery, mentionRoster]);

  // Open for a live token even with no matches, so the reader gets an answer rather
  // than a silently-absent picker.
  const mentionOpen = Boolean(mentionsEnabled && mentionQuery);

  // Clamp the highlight when the list shrinks under it: narrowing three matches to one
  // used to leave the index at 2, so Enter selected nothing.
  useEffect(() => {
    setMentionActive((i) =>
      filteredUsers.length === 0 ? 0 : Math.min(i, filteredUsers.length - 1)
    );
  }, [filteredUsers.length]);

  /** Drop mentions whose `@Name` the author has since deleted. Only ever removes. */
  const reconcileMentions = useCallback((text) => {
    setMentions((prev) => {
      const kept = prev.filter((mention) => hasMention(text, mention.name));
      return kept.length === prev.length ? prev : kept;
    });
  }, []);

  const handleChange = (event) => {
    const text = event.target.value;
    setMessageInput(text);

    emitTyping?.(channelId, true);
    clearTimeout(typingStopTimer.current);
    typingStopTimer.current = setTimeout(() => emitTyping?.(channelId, false), TYPING_IDLE_MS);

    syncMentionQuery(text, event.target.selectionStart);
    reconcileMentions(text);
  };

  /** Caret moved without the text changing — a click or an arrow key in the field. */
  const handleSelect = (event) => syncMentionQuery(event.target.value, event.target.selectionStart);

  const acceptMention = useCallback(
    (user) => {
      if (!user || !mentionQuery) return;

      const node = inputRef.current?.input;
      const caret = node?.selectionStart ?? messageInput.length;
      const { text, caret: nextCaret } = applyMention(
        messageInput,
        mentionQuery.at,
        caret,
        user.name
      );

      setMessageInput(text);
      setMentionQuery(null);
      setMentionActive(0);
      setMentions((prev) =>
        prev.some((mention) => mention.id === user.id)
          ? prev
          : [...prev, { id: user.id, name: user.name }]
      );

      // Deferred a frame: antd re-renders on the value change and would otherwise put
      // the caret back at the end of the field.
      requestAnimationFrame(() => {
        const el = inputRef.current?.input;
        if (!el) return;
        el.focus();
        el.setSelectionRange(nextCaret, nextCaret);
      });
    },
    [mentionQuery, messageInput]
  );

  const handleKeyDown = (event) => {
    if (!mentionOpen) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      // Wraps, so holding the key cycles rather than sticking at an end.
      setMentionActive((i) => (i + 1) % Math.max(filteredUsers.length, 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMentionActive(
        (i) => (i - 1 + Math.max(filteredUsers.length, 1)) % Math.max(filteredUsers.length, 1)
      );
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      if (!filteredUsers.length) return;
      event.preventDefault();
      acceptMention(filteredUsers[mentionActive]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      // Dismiss without clearing the text — the author may want a literal `@`.
      setMentionQuery(null);
    }
  };

  // ── voice note ─────────────────────────────────────────────────────────────

  const formatTime = (seconds) =>
    `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;

  const toggleRecording = async () => {
    try {
      if (isRecording) {
        mediaRecorder?.stop();
        setIsRecording(false);
        clearInterval(intervalRef.current);
        return;
      }

      setAudioUrl(null);
      audioChunks.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);

      recorder.ondataavailable = (event) => audioChunks.current.push(event.data);

      recorder.onstop = async () => {
        // MediaRecorder emits webm/ogg/mp4 (never mp3) — use the real mime type and a
        // matching extension so playback works and S3 serves the right content-type.
        const mimeType = recorder.mimeType || 'audio/webm';
        const ext = mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'm4a' : 'webm';
        const audioBlob = new Blob(audioChunks.current, { type: mimeType });
        setAudioUrl(audioBlob);
        try {
          const formData = new FormData();
          formData.append('media', audioBlob, `recording.${ext}`);
          await uploadChatMedia(formData);
        } catch {
          setAudioUrl(null);
          Utils.showMessage('error', 'Failed to upload the audio message. Please try again.');
        }
        audioChunks.current = [];
        // Release the microphone. Left running, the browser kept showing the
        // recording indicator after the note had been sent.
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setElapsedTime(0);
      intervalRef.current = setInterval(() => {
        setElapsedTime((previous) => {
          const next = previous + 1;
          if (next >= 60) {
            recorder.stop();
            setIsRecording(false);
            Utils.showMessage('error', 'Voice messages are limited to 1 minute');
            clearInterval(intervalRef.current);
          }
          return next;
        });
      }, 1000);
    } catch {
      // Usually a denied or absent microphone. Swallowed silently, the button
      // simply did nothing.
      // If getUserMedia resolved and MediaRecorder construction is what threw, the
      // capture is already open — release it rather than leaving it to the unmount.
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setIsRecording(false);
      Utils.showMessage(
        'error',
        'Could not access the microphone. Check your browser permissions.'
      );
    }
  };

  // ── attachments ────────────────────────────────────────────────────────────

  const MAX_ATTACHMENTS = 5;
  const MAX_VIDEO_SECONDS = 60;

  const handleFileInputChange = (event) => {
    const files = Array.from(event.target.files);
    if (files?.length > MAX_ATTACHMENTS) {
      // Keyed, so selecting a batch cannot stack one toast per file.
      Utils.showMessage('warning', `You can attach up to ${MAX_ATTACHMENTS} files at a time.`, {
        key: 'attachment-limit',
      });
      return;
    }
    if (selectedImages?.length <= MAX_ATTACHMENTS) {
      files?.forEach((file) => {
        if (file.type.startsWith('image/')) {
          setSelectedImages((previous) => [...previous, file]);
        }
        if (file.type.startsWith('video/')) {
          const videoElement = document.createElement('video');
          videoElement.src = URL.createObjectURL(file);
          videoElement.onloadedmetadata = () => {
            if (videoElement.duration > MAX_VIDEO_SECONDS) {
              Utils.showMessage(
                'warning',
                `Videos must be under ${MAX_VIDEO_SECONDS} seconds. That clip was not attached.`,
                { key: 'attachment-video-length' }
              );
            } else {
              setSelectedImages((previous) => [...previous, file]);
            }
            // The object URL was never released, so every rejected clip leaked its
            // decoded blob for the lifetime of the tab.
            URL.revokeObjectURL(videoElement.src);
          };
        }
      });
    }
    event.target.value = null;
  };

  // ── send ───────────────────────────────────────────────────────────────────

  const canSend = Boolean(messageInput?.trim() || audioUrl || selectedImages.length);

  const sendMessage = () => {
    if (isSecret) {
      const regex = new RegExp(
        '^(?=.*(?:facebook.com|instagram.com|twitter.com|linkedin.com|youtube.com|tiktok.com|snapchat.com|telegram.me|t.me))|(?=.*([\\d][\\s-]?[\\d][\\s-]?[\\d][\\s-]?[\\d][\\s-]?[\\d][\\s-]?[\\d][\\s-]?[\\d][\\s-]?[\\d][\\s-]?[\\d])).*$',
        'i'
      );
      if (regex.test(messageInput)) {
        Utils.showMessage(
          'error',
          "You're not allowed to share social links or 9 Digit UID within this secret group!"
        );
        return;
      }
    }

    if (!(selectedImages.length || messageInput?.length || audioUrl)) return;

    // An emit into a PARKED connection is queued against a connect that will never
    // come, and Socket.IO reports nothing — which is how a message that was never sent
    // came to look delivered. A socket that is merely reconnecting is fine: it
    // flushes, acknowledgements included.
    if (!isRealtimeUsable(socket)) {
      Utils.showMessage('error', SOCKET_DOWN_MESSAGE, { key: 'socket-offline' });
      return;
    }

    // Held so a rejected send can hand the text back rather than eating it.
    const draft = messageInput;

    try {
      clearTimeout(typingStopTimer.current);
      emitTyping?.(channelId, false);

      socket.emit(
        'message:send',
        {
          sender_id: userData?.id,
          channel_id: channelId,
          reply_id: replyOnMsg?.id ?? replyOnMsg?.message_id ?? replyOnMsg?.message_ID ?? null,
          message: selectedImages.length > 0 ? null : messageInput,
          type: audioUrl ? 'AUDIO' : selectedImages.length > 0 ? 'MEDIA' : 'TEXT',
          media: chatMedia ?? null,
          current_location: null,
          duration: audioUrl ? elapsedTime : null,
          // `groupName` is deliberately absent: the server reads the channel row for
          // it, so a client cannot choose the text that lands in someone else's
          // notification.
          mentionIds: mentions.map((mention) => mention.id),
          isSecret,
        },
        (item) => {
          // A refusal (not a member, broadcast-only group, reply in a broadcast) and a
          // server-side failure both come back here. Rendering them produced a row of
          // undefineds — a blank bubble reading as sent.
          const failure = Utils.ackError(item);
          if (failure) {
            Utils.showMessage('error', failure);
            // Only if the composer is still empty: the sender may have started typing
            // something else while the ack was in flight.
            setMessageInput((current) => current || draft);
            return;
          }

          dispatch(
            MsgAction.SendMsg([
              {
                channel_id: item?.response?.channel_id,
                id: item?.response?.id,
                media: item?.response?.media,
                reply_id: item?.response?.reply_id,
                replied_message: item?.response?.replied_message,
                message: item?.response?.message,
                type: item?.response?.type,
                createdAt: item?.response?.createdAt,
                statusMsg: item?.response?.status,
                sender: item?.response?.sender,
                sender_id: item?.response?.sender_id,
                chat_reactions: item?.response?.chat_reactions,
                deleted_by_id: item?.response?.deleted_by_id,
              },
            ])
          );
          onSent?.();
        }
      );

      setMessageInput('');
      setSelectedImages([]);
      setAudioUrl(null);
      setMentions([]);
      setMentionQuery(null);
      onClearReply?.();
    } catch {
      // Was an empty block, so a throw here left the composer looking as though the
      // message had gone.
      Utils.showMessage('error', 'Message could not be sent');
      setMessageInput((current) => current || draft);
    }
  };

  if (!canPost) return null;

  const attachmentsBusy = selectedImages.length > 0;
  const textBusy = messageInput?.length > 0;

  const suffix = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span
        title={isRecording ? 'Stop recording' : 'Voice note'}
        className={`composer-icon-btn ${attachmentsBusy || textBusy ? 'cursor-not-allowed' : ''}`}
        style={
          isRecording ? { color: 'var(--accent)', animation: 'recPulse 1s infinite' } : undefined
        }
        onClick={attachmentsBusy || textBusy ? undefined : toggleRecording}
      >
        {isRecording ? <FaRecordVinyl size={18} /> : <FaMicrophone size={18} />}
      </span>
      <span
        title="Attach"
        className={`composer-icon-btn ${isRecording || audioUrl || textBusy ? 'cursor-not-allowed' : ''}`}
        style={{
          pointerEvents: selectedImages.length >= MAX_ATTACHMENTS ? 'none' : undefined,
          opacity: selectedImages.length >= MAX_ATTACHMENTS ? 0.5 : undefined,
        }}
        onClick={
          isRecording || audioUrl || textBusy
            ? undefined
            : () => document.getElementById('fileInput')?.click()
        }
      >
        <IoAttach size={20} />
      </span>
    </span>
  );

  const sendButton = canSend ? (
    <button
      type="button"
      title="Send"
      className={`composer-send-btn ${uploadMediaLoader || isRecording ? 'cursor-not-allowed' : ''}`}
      disabled={uploadMediaLoader || isRecording}
      onClick={uploadMediaLoader ? undefined : sendMessage}
    >
      <IoSend size={18} />
    </button>
  ) : (
    <Tooltip placement="top" title="Schedule message">
      <button type="button" className="composer-send-btn" onClick={onOpenSchedule}>
        <IoCalendarSharp size={17} />
      </button>
    </Tooltip>
  );

  return (
    <>
      {replyOnMsg && (
        <Row
          style={{ backgroundColor: Colors.backGroundColor }}
          className={`${selectedImages?.length > 0 ? 'mb-16' : ''}`}
        >
          <Col span={23} className="mt-3 h-16">
            <div
              style={{ backgroundColor: Colors.White }}
              className="p-2 border-l-4 border-l-[#f56469]"
            >
              <Col span={24}>
                <Text.Basic
                  style={{ fontWeight: '600', fontSize: '0.8rem' }}
                  className="text-[#2B3674]"
                  // A secret group shows the anonymous handle, never the real name.
                  text={isSecret ? replyOnMsg?.user?.member_mode : replyOnMsg?.user?.first_name}
                />
              </Col>
              {replyOnMsg?.type == 'TEXT' ? (
                <Text.Basic
                  style={{ fontWeight: '400', fontSize: '0.8rem' }}
                  className="text-[#2B3674]"
                  text={replyOnMsg?.message}
                />
              ) : replyOnMsg?.type == 'AUDIO' ? (
                <span>
                  <Avatar size={30} shape="square" icon={<GiSoundWaves />} />
                  <Text.Basic
                    style={{ fontWeight: '400', fontSize: '0.8rem' }}
                    className="text-[#2B3674] ml-2"
                    text={'Audio'}
                  />
                </span>
              ) : (
                <span>
                  <Avatar size={20} shape="square" icon={<UserOutlined />} />
                  <Text.Basic
                    style={{ fontWeight: '400', fontSize: '0.8rem' }}
                    className="text-[#2B3674] ml-2"
                    text={'MEDIA'}
                  />
                </span>
              )}
            </div>
          </Col>
          <Col span={1} className="reply-cancel">
            <MdCancel
              size={20}
              style={{ color: Colors.Black }}
              className="cursor-pointer"
              onClick={onClearReply}
            />
          </Col>
        </Row>
      )}

      {mentionOpen && (
        <MentionSuggestions
          users={filteredUsers}
          query={mentionQuery?.query ?? ''}
          activeIndex={mentionActive}
          onSelect={acceptMention}
          onActiveIndexChange={setMentionActive}
          listboxId="group-mention-listbox"
        />
      )}

      <div className="composer-row">
        <ComposerEmojiPicker
          onSelect={(emoji) => setMessageInput((previous) => `${previous ?? ''}${emoji}`)}
          disabled={attachmentsBusy || Boolean(audioUrl) || isRecording}
        />
        <Input
          maxLength={isSecret ? 3000 : undefined}
          ref={inputRef}
          onChange={handleChange}
          onSelect={handleSelect}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={mentionOpen}
          aria-controls={mentionOpen ? 'group-mention-listbox' : undefined}
          aria-activedescendant={
            mentionOpen && filteredUsers[mentionActive]
              ? `group-mention-listbox-opt-${filteredUsers[mentionActive].id}`
              : undefined
          }
          // Guarded rather than relying on preventDefault in onKeyDown: rc-input calls
          // `onPressEnter` BEFORE the `onKeyDown` prop, so by the time the picker could
          // cancel the event the message has already gone. Enter belongs to the picker
          // while it is open.
          onPressEnter={
            canSend && !uploadMediaLoader && !isRecording
              ? () => {
                  if (mentionOpen) return;
                  sendMessage();
                }
              : undefined
          }
          value={attachmentsBusy || audioUrl || isRecording ? '' : messageInput}
          size="medium"
          placeholder={
            attachmentsBusy
              ? 'Press the send button to send images'
              : audioUrl
                ? 'Press the send button to send audio'
                : isRecording
                  ? 'Recording...'
                  : 'Type Message'
          }
          prefix={isRecording ? formatTime(elapsedTime) : ''}
          suffix={suffix}
          style={{
            background: 'var(--panel-2)',
            border: '1.5px solid var(--border)',
            borderRadius: 'var(--composer-r)',
          }}
          className="background-none"
          disabled={attachmentsBusy || Boolean(audioUrl)}
        />
        {sendButton}
      </div>

      <input
        id="fileInput"
        type="file"
        style={{ display: 'none' }}
        accept="image/*"
        multiple
        onChange={handleFileInputChange}
      />

      <ComposerAttachments
        images={selectedImages}
        onRemoveImage={(index) =>
          setSelectedImages((previous) => previous.filter((_, i) => i !== index))
        }
        onClearImages={() => setSelectedImages([])}
        audio={audioUrl}
        onRemoveAudio={() => setAudioUrl(null)}
        recordedSeconds={elapsedTime}
        busy={uploadMediaLoader}
      />
    </>
  );
};

export default memo(GroupComposer);
